import type { ReactNode } from "react"
import type { ColumnDef } from "@tanstack/react-table"

import type { StatusTone } from "@/lib/status-colors"

export type SortDirection = "asc" | "desc"

/**
 * What the server actually ordered by, which is not always what was asked for.
 * A key the query's whitelist rejected comes back as `column: null`, so the
 * header draws no arrow rather than pointing at a column that is not ordering
 * anything. Mirrors `Sortable#applied_sort`.
 */
export type AppliedSort = {
  column: string | null
  direction: SortDirection
}

export type FilterOption = {
  value: string
  label: string
}

export type DateRangeValue = {
  from: string | null
  to: string | null
}

export type AppliedFilters = Record<string, string[] | DateRangeValue>

/**
 * What may travel in the query string. Structurally a subset of Inertia's
 * `FormDataConvertible`, spelled out here because `@inertiajs/core` is a
 * transitive dependency and pnpm does not link it for the app to import.
 */
export type VisitValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | VisitValue[]
  | { [key: string]: VisitValue }

export type VisitParams = Record<string, VisitValue>

/**
 * How a column is narrowed, which is a fact about the column's cardinality
 * rather than a styling choice.
 *
 * `enum` is a closed set — a status, a payment method, a role. Its options are
 * a constant in the Rails model, so the dropdown costs no queries.
 * `dateRange` has no options to fetch at all.
 *
 * There is deliberately no "list of every distinct value" filter. A dropdown of
 * forty thousand checkboxes is not a filter, and a column with that many values
 * wants the search box instead.
 */
export type ColumnFilter =
  | { kind: "enum"; options: FilterOption[]; multiple?: boolean }
  | { kind: "dateRange" }

/**
 * How a cell is rendered. The type lives on the column — declared once — while
 * the value stays a plain primitive in the row data. Putting the value here
 * instead would mean rebuilding the column definitions per row, and TanStack
 * resets sorting, filters and selection whenever those change identity.
 *
 * Alignment, weight and colour follow from the type, so a currency column
 * cannot be right-aligned on one screen and left on another. See DESIGN.md.
 */
export type Field<TData> =
  /**
   * The column that says *which record this is*: a name, a `#id`, a class name.
   *
   * `font-medium` (500), never bold. DESIGN.md reserves 700 for marketing and
   * 600 for figures, and there are only two weights to spend — if the name and
   * the total are both 600, neither wins. Identity is already privileged by
   * being the first column.
   *
   * Exactly one per table. It renders as the row's `th`, it cannot be hidden,
   * and it becomes the headline of the mobile card.
   */
  | {
      type: "title"
      href?: (row: TData) => string
      truncate?: boolean
      /**
       * For identities that are machine data — an event id, a bill reference,
       * a job key. DESIGN.md reserves monospace for exactly those, and a table
       * whose identity column is one should not have to choose between reading
       * correctly and being announced correctly.
       */
      mono?: boolean
    }
  | { type: "text"; truncate?: boolean }
  | { type: "number"; decimals?: number }
  | { type: "currency"; currency?: string }
  | { type: "percent"; decimals?: number }
  | { type: "date"; timeZone?: string }
  | { type: "datetime"; timeZone?: string }
  | { type: "boolean" }
  /** `tones` maps the stored value to a status token; `labelKey` names the field holding the localized label. */
  | { type: "status"; tones: Record<string, StatusTone>; labelKey?: string }
  | { type: "code" }
  | { type: "link"; href: (row: TData) => string }
  /**
   * Row controls, declared once and rendered in both the table and the mobile
   * feed. Pages used to write the buttons twice — a `cell` for the table and
   * the same JSX again inside `renderMobileCard` — which is how the two drift.
   */
  | { type: "actions"; render: (row: TData) => ReactNode }

/**
 * Where a column goes when the table becomes a card feed. The `title` column is
 * always the headline and never needs to say so.
 */
export type MobileSlot = "secondary" | "meta" | "hidden"

export type DataColumn<TData> = ColumnDef<TData, unknown> & {
  field?: Field<TData>
  /**
   * The key the server's `sortable` whitelist declares for this column. Without
   * it the column is not sortable, which is the point: a header cannot offer an
   * ordering the backend has no way to apply.
   */
  sortKey?: string
  /** The key the server's `filterable` whitelist declares, paired with the widget. */
  filterKey?: string
  filter?: ColumnFilter
  mobile?: MobileSlot
}

export type ServerPagination = {
  page: number
  perPage: number
  totalPages: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
}

type CommonProps<TData> = {
  columns: DataColumn<TData>[]
  data: TData[]
  /**
   * The IANA zone every date and datetime cell is rendered in — the record's
   * zone, from backend props, never the browser's. See .claude/rules/react.md.
   */
  timeZone?: string
  surface?: "plain" | "card"
  emptyMessage?: string
  perPageOptions?: number[]
  /**
   * Stable per-row hook for the Playwright suite, on the `tr` and on the mobile
   * card alike. `.claude/rules/testing.md` requires these and forbids stripping
   * them, so the primitive has to be able to carry one.
   */
  rowTestId?: (row: TData) => string
  /** Escape hatch. Without it the mobile feed is generated from the column types. */
  renderMobileCard?: (row: TData) => ReactNode
}

export type ClientTableProps<TData> = CommonProps<TData> & {
  mode?: "client"
  /** Column id to filter on as you type. Folds accents, matching the server. */
  searchKey?: string
  searchPlaceholder?: string
}

export type ServerTableProps<TData> = CommonProps<TData> & {
  mode: "server"
  /** Path the table navigates to when the operator sorts, filters, searches or pages. */
  url: string
  pagination: ServerPagination
  sort: AppliedSort
  /** Omitted by a table that declares no column filters. */
  filters?: AppliedFilters
  search?: string
  searchPlaceholder?: string
  /** Props Inertia reloads on each visit. Everything else on the page stays put. */
  reloadOnly?: string[]
  error?: string | null
}

/**
 * Client and server are separate shapes rather than one shape with optional
 * halves, and that is load-bearing. In server mode the table holds one page of
 * rows, so a client-side sort would reorder those rows and present the result
 * as if it had ordered the whole table. The union makes that combination
 * impossible to express instead of leaving it to review.
 */
export type DataTableProps<TData> = ClientTableProps<TData> | ServerTableProps<TData>

export function isServerMode<TData>(
  props: DataTableProps<TData>
): props is ServerTableProps<TData> {
  return props.mode === "server"
}
